import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, ErrorCarga, Vacio } from '@/components/Estados';
import { AvisoSinGuardar, PaginaEditor } from '@/components/PaginaEditor';
import { usePestana } from '@/hooks/usePestana';
import { fechaLarga } from '@/lib/formato';

import { ArchivoUnico } from '../comun/ArchivoUnico';
import { comunesDe, cuerpoComunes } from '../comun/comunes';
import { DatosComunes } from '../comun/DatosComunes';
import { LicenciasElemento } from '../comun/Paneles';
import { PestanaPresentacion, PestanaResumenes, PestanaUltra } from './PestanaLigados';
import { PestanaRecursos, PestanaVideos } from './PestanaListas';
import { PestanaPregunta } from './PestanaPregunta';
import { rutaClase, useClase, useClaseMutacion, type Clase } from './queries';

/** Pestañas en el orden en que las ve el alumno, más las de administración al final. */
const PESTANAS = [
  ['general', 'General'],
  ['caso', 'Caso clínico'],
  ['master', 'Master Class'],
  ['ultra', 'Ultra Resumen'],
  ['resumenes', 'Resúmenes GPC'],
  ['ingles', 'Inglés Médico'],
  ['guias', 'Guías GPC'],
  ['plus', 'ENARM Plus'],
  ['presentacion', 'Presentación'],
  ['quiz', 'Quiz'],
  ['bibliografia', 'Bibliografía'],
  ['temario', 'Temario'],
  ['licencias', 'Licencias'],
  ['resenas', 'Reseñas'],
] as const;

function General({ c }: { c: Clase }) {
  const editable = useCan('contenido', 'editar');
  const inicial = {
    comunes: comunesDe(c),
    isPublished: c.isPublished,
    isWorkshop: c.isWorkshop,
    cover: c.cover,
  };
  const [f, setF] = useState(inicial);
  const sucio = JSON.stringify(f) !== JSON.stringify(inicial);
  const guardar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        api.PATCH('/api/v1/admin/classes/{id}', {
          ...rutaClase(c.id),
          body: {
            ...cuerpoComunes(f.comunes),
            isPublished: f.isPublished,
            isWorkshop: f.isWorkshop,
            coverPath: f.cover?.path ?? null,
          },
        }),
      ),
    'Clase guardada.',
  );
  return (
    <form
      className="grid gap-6 xl:grid-cols-[1.4fr_1fr]"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate(undefined);
      }}
    >
      <AvisoSinGuardar sucio={sucio} />
      <section className="panel-vidrio space-y-4">
        <DatosComunes
          valor={f.comunes}
          onCambio={(x) => setF({ ...f, comunes: { ...f.comunes, ...x } })}
          nombreMax={120}
          especialidadRequerida
          campos={{ descripcion: false }}
          deshabilitado={!editable}
        />
        <fieldset disabled={!editable} className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              checked={f.isPublished}
              onChange={(e) => setF({ ...f, isPublished: e.target.checked })}
            />
            Visible para el alumno
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 accent-[var(--color-azul-600)]"
              checked={f.isWorkshop}
              onChange={(e) => setF({ ...f, isWorkshop: e.target.checked })}
            />
            Es taller
          </label>
        </fieldset>
        {!c.isWorkshop && c.topics.length === 0 && (
          <p className="flex items-start gap-2 rounded-lg bg-[var(--pista)] p-3 text-sm">
            <AlertTriangle size={18} className="shrink-0 text-amarillo-texto" aria-hidden />
            Ningún tema del temario apunta a esta clase: aunque esté visible, el alumno no la verá.{' '}
            <Link to="/contenido/temario" className="font-semibold underline">
              Ir al temario
            </Link>
          </p>
        )}
      </section>
      <section className="panel-vidrio">
        <ArchivoUnico
          destino="clase"
          ownerId={c.id}
          tipos={['image']}
          valor={f.cover}
          quitable
          deshabilitado={!editable}
          etiqueta="Portada"
          onCambio={(cover) => setF({ ...f, cover })}
        />
      </section>
      {editable && (
        <div className="flex justify-end xl:col-span-2">
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={!sucio || guardar.isPending}
          >
            Guardar
          </button>
        </div>
      )}
    </form>
  );
}

function Bibliografia({ c }: { c: Clase }) {
  const editable = useCan('contenido', 'editar');
  const [texto, setTexto] = useState(c.bibliography ?? '');
  const sucio = texto !== (c.bibliography ?? '');
  const guardar = useClaseMutacion(
    c.id,
    () =>
      unwrap<Clase>(
        api.PATCH('/api/v1/admin/classes/{id}', {
          ...rutaClase(c.id),
          body: { bibliography: texto || null },
        }),
      ),
    'Bibliografía guardada.',
  );
  return (
    <form
      className="panel-vidrio max-w-3xl space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        guardar.mutate(undefined);
      }}
    >
      <AvisoSinGuardar sucio={sucio} />
      <Campo
        etiqueta="Bibliografía"
        ayuda="Texto plano. El alumno ve la pestaña solo si tiene al menos 10 caracteres."
      >
        <textarea
          className="input-vidrio min-h-60"
          value={texto}
          disabled={!editable}
          maxLength={20000}
          onChange={(e) => setTexto(e.target.value)}
        />
      </Campo>
      {editable && (
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={!sucio || guardar.isPending}
          >
            Guardar
          </button>
        </div>
      )}
    </form>
  );
}

function Resenas({ c }: { c: Clase }) {
  const r = c.reviews;
  return (
    <div className="panel-vidrio max-w-3xl space-y-4">
      <p className="text-sm text-texto-2">
        {r.students} alumno(s) con avance · {r.count} calificación(es)
        {r.average !== null && ` · promedio ${r.average.toFixed(2)} ★`}
      </p>
      {r.items.length === 0 && <Vacio titulo="Sin reseñas" />}
      <ul className="space-y-2">
        {r.items.map((x, i) => (
          <li key={i} className="rounded-lg border border-[var(--separador)] p-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{x.userName}</span>
              {x.stars !== null && (
                <span className="flex text-amarillo-texto" aria-label={`${x.stars} estrellas`}>
                  {Array.from({ length: x.stars }, (_, k) => (
                    <Star key={k} size={14} fill="currentColor" aria-hidden />
                  ))}
                </span>
              )}
              <span className="ml-auto text-xs text-texto-3">
                {x.reviewedAt && fechaLarga(x.reviewedAt)}
              </span>
            </div>
            {x.comment && <p className="mt-1 whitespace-pre-wrap">{x.comment}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Editor({ c }: { c: Clase }) {
  const navigate = useNavigate();
  const editable = useCan('contenido', 'editar');
  const [pestana, setPestana] = usePestana(PESTANAS);
  const [borrando, setBorrando] = useState(false);
  const { proteger, dialogo } = useStepUp();
  const qc = useQueryClient();
  const borrar = useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/v1/admin/classes/{id}', rutaClase(c.id))),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contenido'] }),
  });
  const k = c.updatedAt ?? '';
  return (
    <PaginaEditor
      volver={{ a: '/contenido/clases', texto: 'Clases' }}
      titulo={c.name}
      descripcion={`Clase #${c.id}${c.code ? ` · ${c.code}` : ''} · ${c.isPublished ? 'visible' : 'oculta'}${c.isWorkshop ? ' · taller' : ''}${c.updatedAt ? ` · actualizada ${fechaLarga(c.updatedAt)}` : ''}`}
      acciones={
        editable && (
          <button type="button" className="btn-cs btn-vidrio" onClick={() => setBorrando(true)}>
            <Trash2 size={16} aria-hidden /> Borrar
          </button>
        )
      }
      pestanas={PESTANAS}
      pestana={pestana}
      onPestana={setPestana}
    >
      {pestana === 'general' && <General key={k} c={c} />}
      {pestana === 'caso' && <PestanaPregunta key={k} c={c} tipo="case" />}
      {pestana === 'master' && <PestanaVideos c={c} />}
      {pestana === 'ultra' && <PestanaUltra key={k} c={c} />}
      {pestana === 'resumenes' && <PestanaResumenes key={k} c={c} />}
      {pestana === 'ingles' && <PestanaRecursos c={c} grupo="english" />}
      {pestana === 'guias' && <PestanaRecursos c={c} grupo="guides" />}
      {pestana === 'plus' && <PestanaRecursos c={c} grupo="plus" />}
      {pestana === 'presentacion' && <PestanaPresentacion key={k} c={c} />}
      {pestana === 'quiz' && <PestanaPregunta key={k} c={c} tipo="quiz" />}
      {pestana === 'bibliografia' && <Bibliografia key={k} c={c} />}
      {pestana === 'temario' && (
        <div className="panel-vidrio max-w-3xl">
          {c.topics.length === 0 ? (
            <Vacio titulo="Ningún tema del temario apunta a esta clase" />
          ) : (
            <ul className="space-y-1 text-sm">
              {c.topics.map((t) => (
                <li key={t.id}>
                  <span className="font-semibold">{t.module}</span>{' '}
                  <span className="text-texto-3">{t.specialtyName ?? ''}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/contenido/temario" className="btn-cs btn-vidrio mt-3">
            Ir al temario
          </Link>
        </div>
      )}
      {pestana === 'licencias' && (
        <div className="panel-vidrio">
          <LicenciasElemento libraryItemId={c.libraryItemId} asignadas={c.membershipIds} />
        </div>
      )}
      {pestana === 'resenas' && <Resenas c={c} />}
      <ConfirmarAccion
        abierto={borrando}
        onCerrar={() => setBorrando(false)}
        titulo={`Borrar «${c.name}»`}
        descripcion="Se borra con sus videos, caso, quiz y archivos ligados, y sale de las licencias. Si hay alumnos con avance o está en el temario, no se puede borrar: ocúltala en su lugar."
        textoBoton="Borrar"
        peligro
        ocupado={borrar.isPending}
        onConfirmar={() =>
          void proteger(() => borrar.mutateAsync())
            .then(() => {
              toast.success('Clase borrada.');
              void navigate('/contenido/clases');
            })
            .catch((e: unknown) => toast.error(mensajeDe(e)))
        }
      />
      {dialogo}
    </PaginaEditor>
  );
}

export function ClasePage() {
  const id = Number(useParams().id);
  const q = useClase(id);
  return (
    <PermissionGate seccion="contenido">
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && <Editor key={q.data.id} c={q.data} />}
    </PermissionGate>
  );
}
